import { TranslatableTraining } from '@shared/training-translations/training-translations.controller';
import {
  TrainingLanguageAvailability,
  resolveLearnerLanguage,
} from '@shared/training-translations/training-availability.util';
import {
  TrainingTranslation,
  TrainingTranslationFields,
} from '@models/training-translation.model';

// Snapshot of a training's authoring-language (source) display fields, captured before any overlay
// so we can always fall back to it when switching languages.
export interface TrainingSourceContent {
  name: string;
  description: string;
  coverImage: string | null;
}

// Capture the source (authoring-language) title, description and cover before overlaying a
// translation. Call this once, right after the training is loaded.
export function captureTrainingSource(
  training: TranslatableTraining,
): TrainingSourceContent {
  return {
    name: training.name,
    description: training.description,
    coverImage: training.coverImage ?? null,
  };
}

/**
 * A standalone label's SOURCE text, parsed out of the activity's content JSON. A label with
 * no source text has nothing to translate: no warning ring, no place in the counter.
 */
export function labelSourceText(
  contentJSON: string | null | undefined,
): string {
  try {
    const labelText: unknown = JSON.parse(contentJSON ?? '')?.labelText;
    return typeof labelText === 'string' ? labelText.trim() : '';
  } catch {
    return '';
  }
}

// Overlay `language`'s translation onto the training's display fields so every downstream
// view reflects it; fields the translation leaves empty fall back to the source snapshot.
export function applyTrainingTranslation(
  training: TranslatableTraining,
  source: TrainingSourceContent,
  language: string,
): void {
  const translation =
    language && language !== training.language
      ? (training.trainingTranslations ?? []).find(
          record => record.language === language,
        )
      : undefined;

  training.name = translation?.fields?.name || source.name;
  training.description = translation?.fields?.description || source.description;
  training.coverImage = translation?.coverImage || source.coverImage;
}

// The persisted translation record for `language`, or null for source / an untranslated
// language. `translations` overrides where the caller keeps its own copy of the rows.
export function trainingTranslationRecord(
  training: TranslatableTraining,
  language: string,
  translations: readonly TrainingTranslation[] = training.trainingTranslations ??
    [],
): TrainingTranslation | null {
  if (!language || language === training.language) {
    return null;
  }
  return translations.find(record => record.language === language) ?? null;
}

// The translation `fields` map for `language`, or null for source / an untranslated language.
// Lets views resolve per-key content with per-key fallback, without mutating the source.
export function trainingTranslationFields(
  training: TranslatableTraining,
  language: string,
): TrainingTranslationFields | null {
  return trainingTranslationRecord(training, language)?.fields ?? null;
}

/**
 * A training's display fields in `language` WITHOUT touching the model: each field
 * translated, falling back to the training's own (source) value.
 *
 * Prefer this over {@link applyTrainingTranslation} for any training the page does not
 * own. Records are SHARED — the datastore reuses a record already in its store and
 * re-extends its attributes from every payload that includes it, so an overlay written
 * onto `name`/`coverImage` is silently reverted by an unrelated later request.
 */
export function trainingDisplayContent(
  training: TranslatableTraining,
  language: string,
  translations: readonly TrainingTranslation[] = training.trainingTranslations ??
    [],
): TrainingSourceContent {
  const record = trainingTranslationRecord(training, language, translations);
  return {
    name: record?.fields?.name || training.name,
    description: record?.fields?.description || training.description,
    coverImage: record?.coverImage || training.coverImage || null,
  };
}

/**
 * A training's display fields as a LEARNER sees them on any list, card, tile or
 * filter: `language`'s translation when the training offers that language and it is
 * visible to learners, its own source text otherwise. Non-mutating like
 * {@link trainingDisplayContent}.
 *
 * `training_translations` must be INCLUDED in the query, or every training reads as
 * untranslated and falls back to its source.
 */
export function learnerDisplayContent(
  training: TranslatableTraining | null | undefined,
  language: string,
): TrainingSourceContent | null {
  if (!training) {
    return null;
  }
  return trainingDisplayContent(
    training,
    resolveLearnerLanguage(training as TrainingLanguageAvailability, language),
  );
}

/**
 * A training's display name in `language` WITHOUT touching the model — '' when
 * that language has no translated name, so callers fall back to the source.
 *
 * Authoring lists use this instead of `applyTrainingTranslation`: overwriting
 * `name` on a Course/ILTCourse the page did not load for editing would put the
 * translated text on the source record. No learner-availability gate either —
 * an author edits a language that is still hidden from learners.
 */
export function translatedTrainingName(
  training: TranslatableTraining,
  language: string,
): string {
  return (trainingTranslationFields(training, language)?.name ?? '').trim();
}
